import type { RoomAsset } from "./types";

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeFootprintDimension(value: number | undefined): number {
  return Math.max(1, Math.round(finiteOr(value, 1)));
}

export function normalizeCardinalRotationDegrees(rotation?: number): number {
  const raw = finiteOr(rotation, 0);
  const snapped = Math.round(raw / 90) * 90;
  return ((snapped % 360) + 360) % 360;
}

export function resolveFurnitureFootprint(
  blockWidth: number | undefined,
  blockDepth: number | undefined,
  rotation: number | undefined
): { width: number; depth: number } {
  const baseWidth = normalizeFootprintDimension(blockWidth);
  const baseDepth = normalizeFootprintDimension(blockDepth);
  const normalizedRotation = normalizeCardinalRotationDegrees(rotation);
  if (normalizedRotation === 90 || normalizedRotation === 270) {
    return { width: baseDepth, depth: baseWidth };
  }
  return { width: baseWidth, depth: baseDepth };
}

export function resolveFurnitureFootprintForAsset(
  asset: Pick<RoomAsset, "blockWidth" | "blockDepth" | "rotation">
): { width: number; depth: number } {
  return resolveFurnitureFootprint(
    asset.blockWidth,
    asset.blockDepth,
    asset.rotation
  );
}

export function resolveFurnitureFootprintCellsAt(
  centerX: number,
  centerZ: number,
  width: number,
  depth: number
): Array<[number, number]> {
  const resolvedWidth = normalizeFootprintDimension(width);
  const resolvedDepth = normalizeFootprintDimension(depth);
  const startX = Math.round(finiteOr(centerX, 0)) - Math.floor(resolvedWidth / 2);
  const startZ = Math.round(finiteOr(centerZ, 0)) - Math.floor(resolvedDepth / 2);
  const cells: Array<[number, number]> = [];
  for (let dx = 0; dx < resolvedWidth; dx += 1) {
    for (let dz = 0; dz < resolvedDepth; dz += 1) {
      cells.push([startX + dx, startZ + dz]);
    }
  }
  return cells;
}

export function resolveFurnitureFootprintCellsForAsset(
  asset: Pick<RoomAsset, "x" | "z" | "blockWidth" | "blockDepth" | "rotation">
): Array<[number, number]> {
  const footprint = resolveFurnitureFootprintForAsset(asset);
  return resolveFurnitureFootprintCellsAt(
    asset.x,
    asset.z,
    footprint.width,
    footprint.depth
  );
}

export function resolveEvenFootprintRenderOffset(
  width: number,
  depth: number
): { x: number; z: number } {
  const resolvedWidth = normalizeFootprintDimension(width);
  const resolvedDepth = normalizeFootprintDimension(depth);
  return {
    x: resolvedWidth % 2 === 0 ? -0.5 : 0,
    z: resolvedDepth % 2 === 0 ? -0.5 : 0,
  };
}

export function resolveFurnitureRenderAlignmentOffset(
  asset: Pick<RoomAsset, "blockWidth" | "blockDepth" | "rotation">
): { x: number; z: number } {
  const footprint = resolveFurnitureFootprintForAsset(asset);
  return resolveEvenFootprintRenderOffset(footprint.width, footprint.depth);
}
